import React, { useEffect } from 'react';

import SEO from '../SEO';import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { CheckCircle, ArrowRight, AlertTriangle, Shield, Droplets, ThermometerSun, Factory, Anchor } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const CorrosionRust = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const corrosionTypes = [
    {
      title: 'Uniform Corrosion',
      description: 'Even deterioration across metal surfaces exposed to corrosive environments.',
      causes: ['Atmospheric exposure', 'Chemical contact', 'Moisture'],
      solutions: ['Barrier coatings', 'Surface preparation', 'Protective primers']
    },
    {
      title: 'Pitting Corrosion',
      description: 'Localized attack creating small holes that can penetrate deeply into metal.',
      causes: ['Chloride exposure', 'Stagnant conditions', 'Coating failures'],
      solutions: ['Pit filling compounds', 'Chemical-resistant linings', 'Cathodic protection']
    },
    {
      title: 'Galvanic Corrosion',
      description: 'Accelerated corrosion when dissimilar metals are in electrical contact.',
      causes: ['Dissimilar metals', 'Electrolyte presence', 'Poor design'],
      solutions: ['Isolation barriers', 'Protective coatings', 'Material selection']
    },
    {
      title: 'Crevice Corrosion',
      description: 'Intense localized corrosion in shielded areas with restricted flow.',
      causes: ['Gasket areas', 'Lap joints', 'Under deposits'],
      solutions: ['Crevice sealing', 'Design modifications', 'Regular cleaning']
    }
  ];

  const warningSignsData = [
    'Visible rust staining or discoloration',
    'Flaking or bubbling paint/coatings',
    'Pitting or rough surface texture',
    'Structural weakness or thinning',
    'Leaks at joints or welds',
    'White or green deposits on metal'
  ];

  const industries = [
    { icon: Factory, name: 'Manufacturing', issue: 'Process equipment degradation' },
    { icon: Droplets, name: 'Water Treatment', issue: 'Tank and pipe deterioration' },
    { icon: ThermometerSun, name: 'Power Generation', issue: 'Boiler and condenser corrosion' },
    { icon: Anchor, name: 'Marine', issue: 'Saltwater exposure damage' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title="Corrosion & Rust Solutions" description="Understanding and solving industrial corrosion and rust problems with professional repair solutions." path="/research/corrosion-rust" />
      
      <PageHero
        badge="Research & Learn"
        title="Corrosion & Rust Issues"
        description="Understanding corrosion is the first step to preventing it. Learn about different types of corrosion, warning signs, and proven solutions to protect your industrial assets."
        backgroundKey="corrosion-rust"
        buttonText="Get Corrosion Assessment"
      />

      {/* Corrosion Types */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Types of Corrosion</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {corrosionTypes.map((type, index) => (
              <Card key={index} className="border-l-4 border-l-red-600">
                <CardHeader>
                  <CardTitle className="text-xl">{type.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">{type.description}</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-semibold text-red-700 mb-2">Common Causes:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {type.causes.map((cause, idx) => (
                          <li key={idx}>• {cause}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-green-700 mb-2">Our Solutions:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {type.solutions.map((solution, idx) => (
                          <li key={idx} className="flex items-center">
                            <CheckCircle className="text-green-700 mr-1" size={12} />
                            {solution}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Warning Signs of Corrosion</h2>
              <p className="text-gray-600 mb-8">
                Early detection is critical. Look for these warning signs during regular inspections 
                to catch corrosion before it causes serious damage or equipment failure.
              </p>
              <div className="space-y-3">
                {warningSignsData.map((sign, index) => (
                  <div key={index} className="flex items-center bg-white p-3 rounded-lg shadow-sm">
                    <AlertTriangle className="text-orange-500 mr-3 flex-shrink-0" size={20} />
                    <span className="text-gray-700">{sign}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-red-700 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Prevention is Key</h3>
              <p className="text-red-100 mb-6">
                The cost of corrosion prevention is typically 10-20% of the cost of repair after failure. 
                Our proactive approach identifies risks before they become expensive problems.
              </p>
              <Button className="bg-white text-red-700 hover:bg-gray-100" onClick={() => navigate('/maintenance/smart-program')}>
                Learn About SMART Program <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Industries Affected */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industries We Help</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {industries.map((industry, index) => {
              const IconComponent = industry.icon;
              return (
                <div key={index} className="text-center p-6 bg-slate-50 rounded-lg">
                  <IconComponent className="mx-auto text-blue-900 mb-3" size={40} />
                  <h3 className="font-bold text-slate-900 mb-2">{industry.name}</h3>
                  <p className="text-sm text-gray-600">{industry.issue}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Don't Let Corrosion Win</h2>
          <p className="text-blue-100 mb-8">
            Our corrosion experts can assess your facility and recommend the most effective protection strategy.
          </p>
          <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/#contact')}>
            Get In Touch <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CorrosionRust;
